import {
  ApplicationConfig,
  ENVIRONMENT_INITIALIZER,
  ErrorHandler,
  inject,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTranslateLoader, provideTranslateService, TranslateService } from '@ngx-translate/core';

import { routes } from './app.routes';
import { authInterceptor } from './core/network/auth.interceptor';
import { responseInterceptor } from './core/network/response.interceptor';
import { GlobalErrorHandler } from './core/error/global-error-handler';
import {
  EMBEDDED_FALLBACK,
  ResilientTranslateLoader,
} from './core/i18n/resilient-translate-loader';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([authInterceptor, responseInterceptor])),
    provideTranslateService({
      lang: 'en',
      fallbackLang: 'en',
      // Not provideTranslateHttpLoader: that issues a single GET with no retry and no fallback,
      // so one failed /i18n/en.json left the app rendering raw keys for the rest of the session
      // with no way back — reported from production, and only fixed by clearing the cache.
      // See ResilientTranslateLoader for the retry/fallback contract.
      loader: provideTranslateLoader(ResilientTranslateLoader),
    }),
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => {
        const translate = inject(TranslateService);
        // Pre-seed the translation dictionary synchronously so early startup gates
        // (UnsupportedBrowserComponent, UnsupportedDeviceComponent, etc.) render immediately
        // without flashing raw translation keys while /i18n/en.json loads over HTTP.
        // Merged so the real `/i18n/en.json` replaces/extends when it arrives.
        translate.setTranslation('en', EMBEDDED_FALLBACK, true);
      },
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideServiceWorker('ngsw-worker.js', {
      // Active on every deployed build (dev/staging/production) — see
      // `enableServiceWorker`'s doc comment in environment.ts for why this is
      // intentionally NOT gated on `environment.production`.
      enabled: environment.enableServiceWorker,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
